import { useContext, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import AuthContext, { AuthActionTypes, AuthUser } from '../library/context/authContext';
import { useRefreshIdToken } from '../library/hocs/withAuth0/AuthPageComponentWrapper';
import { getUserDetail } from '../library/api/userService';
import { isAuthBypassed } from '../library/utils/appConfig';

/**
 * Hook that provides the current authenticated user and auth state.
 * Automatically fetches user details from the backend once Auth0 auth flow completes.
 *
 * Mirrors chefsteps-ui/src/hooks/use-user/use-user.tsx
 */
const useUser = () => {
  const { state, dispatch } = useContext(AuthContext);
  const { isAuthenticated, isLoading: isAuth0Loading } = useAuth0();
  const { fetchIdToken } = useRefreshIdToken();

  const setUser = useCallback(
    (user: AuthUser | null) => {
      dispatch({ type: AuthActionTypes.SET_USER, payload: user });
    },
    [dispatch]
  );

  // Fetch user details once auth flow is complete and user is authenticated
  useEffect(() => {
    if (isAuthBypassed) return;
    if (!isAuthenticated || !state.isAuthFlowCompleted) return;

    getUserDetail().then((user) => {
      if (user) {
        setUser(user);
        dispatch({ type: AuthActionTypes.SET_IS_AUTHENTICATED, payload: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, state.isAuthFlowCompleted]);

  // Sync isAuthenticated from Auth0 into our context once loading is done
  useEffect(() => {
    if (isAuthBypassed) return;
    if (isAuth0Loading) return;
    dispatch({ type: AuthActionTypes.SET_IS_AUTHENTICATED, payload: isAuthenticated });

    if (!isAuthenticated) {
      setUser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuth0Loading]);

  if (isAuthBypassed) {
    return {
      user: state.user,
      isAuthenticated: true,
      isAuthFlowCompleted: true,
      isStudioPassVip: true,
      setUser,
      fetchIdToken,
    };
  }

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isAuthFlowCompleted: state.isAuthFlowCompleted,
    isStudioPassVip: state.isStudioPassVip,
    setUser,
    fetchIdToken,
  };
};

export default useUser;
